/* global ActionCable,App */
import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import dayjs from "@shared/dayjs";
import { capitalize } from "lodash";
import api from "@shared/phoneDncStatusApi";
import { DncFlag } from "@shared/Sms";
import { tryCatchHandlr } from "@shared/Utilities";
import { ArrowUpRightFromSquareSolidV6, PhoneHangupSolidV6 } from "@shared/v2/Icomoon";
import Button from "@shared/v2/Button";
import formatPhoneNumber from "@shared/FormatPhoneNumber";
import FieldLabel from "@shared/v2/FieldLabel";
import StatusDropdown from "@shared/StatusDropdown/StatusDropdown";
import { produce } from "immer";
import StagesDropDown from "@shared/StagesDropDown";
import Toggle from "@shared/v2/Toggle";
import Modal from "@shared/v2/Modal";
import {
  callStatusIconForPhone,
  mobileCallStatusIcon,
} from "@shared/models/Communications/viewPhoneIcons/CallStatusIcon";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import AddTaskPane from "@shared/AddTaskPane";
import CallStatusBar from "@shared/CallStatusBar";
import { createTask } from "../../Tasks/components/services";
import CallOutcomeAndNotes from "./CallOutcomeAndNotes";
import UpNext from "./UpNext";
import {
  useOutboundCallRecordingSetting,
  useCurrentUser,
  useFeatureFlags,
} from "../../reducers/layoutReducer/selectors";
import IntentDetails from "../../LeadIndex/components/IntentDetails";
import RecordConfirmationModal from "../../Dialer/components/RecordConfirmationModal";
import PhoneStatusIcon from "./PhoneStatusIcon";
import TwoPartyFlag from "@shared/Sms/TwoPartyFlag";

const nameFormat = (name) => (name?.trim().length > 0 ? name : "No Name");

export const callCompleteStatus = ["Completed", "Failed", "Busy", "No-answer", "Canceled"];

const ConferenceCallForm = ({ dialerServiceUrl, dialerServiceToken, closeCallModal, startCall }) => {
  const featureFlags = useFeatureFlags();
  const currentUser = useCurrentUser();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [conferenceCall, setConferenceCall] = useState(null);
  const [websocketsConnected, setWebsocketsConnected] = useState(false);
  const [possibleStaleState, setPossibleStaleState] = useState(false);
  const [currentCallTime, setCurrentCallTime] = useState("0:00:00");
  const [callOutcome, setCallOutcome] = useState({ outcome: null, comment: "" });
  const [endSessionLoading, setEndSessionLoading] = useState(false);
  const [endCallLoading, setEndCallLoading] = useState(false);
  const [endCallErrors, setEndCallErrors] = useState([]);
  const [savingCallOutcome, setSavingCallOutcome] = useState(false);
  const [savingOutcomeErrors, setSavingOutcomeErrors] = useState([]);
  const [savingOutcomeSuccess, setSavingOutcomeSuccess] = useState(null);
  const [savingLeadError, setSavingLeadError] = useState(null);
  const [updatedStatus, setUpdatedStatus] = useState(null);
  const [dncStatuses, setDncStatuses] = useState({});
  const [person, setPerson] = useState({});
  const [isPersonLoading, setPersonLoading] = useState(true);
  const [isPersonStatusLoading, setPersonStatusLoading] = useState(false);
  const [taskToggle, setTaskToggle] = useState(false);
  const [agents, setAgents] = useState([]);
  const [task, setTask] = useState(null);
  const [nextParticipantId, setNextParticipantId] = useState(null);
  const [recorded, setRecorded] = useState(false);
  const [recordToggleDisabled, setRecordToggleDisabled] = useState(false);
  const { outboundRecording } = useOutboundCallRecordingSetting();
  const scrollRef = useRef(null);
  const conferenceCallRef = useRef(conferenceCall);
  conferenceCallRef.current = conferenceCall;

  const currentPhoneNumber = conferenceCall?.current_participant?.phone_number;
  const nextPhoneNumber = conferenceCall?.next_participant?.phone_number;

  const phoneDetails =
    person?.included?.find(
      (item) =>
        item.type === "phone_detail" &&
        item.id !== null &&
        item.attributes?.value === person?.data?.attributes?.primary_phone_number,
    )?.attributes || null;

  const callUnsubscribed =
    phoneDetails?.call_unsubscribed ||
    phoneDetails?.call_soft_unsubscribed ||
    phoneDetails?.call_account_unsubscribed;

  let callStatus;
  if (callUnsubscribed) {
    callStatus = "unsubscribed";
  } else if (!phoneDetails?.sms_textable) {
    callStatus = "voiceOnly";
  } else {
    callStatus = "active";
  }

  const callIcon =
    phoneDetails?.category === "mobile"
      ? mobileCallStatusIcon(callStatus)
      : callStatusIconForPhone(callStatus);

  const getPhoneStatuses = () => {
    api.index([currentPhoneNumber, nextPhoneNumber]).then((res) => {
      setDncStatuses((oldState) => ({ ...oldState, ...res.data }));
    });
  };

  const toggleRecording = (recordingConsent) => {
    setRecordToggleDisabled(true);

    caseTransformingAxios
      .post(`${dialerServiceUrl}/dialer/${!recorded ? "start" : "stop"}_recording`, {
        token: dialerServiceToken,
        ...(!recorded
          ? { recording_consent: featureFlags.callRecordingAdjustments || recordingConsent || false }
          : {}),
      })
      .then(() => {
        setRecordToggleDisabled(false);
        setRecorded(!recorded);
      })
      .catch((err) => {
        if (err.response?.data?.error === "Recording consent is required") {
          setShowConsentModal(true);
        }
      });
  };

  useEffect(() => {
    if (
      outboundRecording === "automatic" &&
      conferenceCall?.current_participant?.humanized_call_status === "In-progress"
    ) {
      toggleRecording();
    }
  }, [outboundRecording, conferenceCall?.current_participant?.humanized_call_status]);

  useEffect(() => {
    let timer = null;

    const updateCallTime = () => {
      if (
        conferenceCallRef?.current?.current_participant?.humanized_call_status === "In-progress" &&
        conferenceCallRef?.current?.current_participant?.call_answered_timestamp
      ) {
        const callTime = dayjs
          .utc(
            dayjs
              .duration(
                dayjs().diff(dayjs(conferenceCallRef?.current?.current_participant?.call_answered_timestamp)),
              )
              .asMilliseconds(),
          )
          .format("H:mm:ss");
        setCurrentCallTime(callTime);
      }
    };

    const manageSubscriptionAndTimer = () => {
      if (typeof App !== "undefined" && App.phone_call) {
        App.phone_call.unsubscribe();
      }
      clearInterval(timer);
    };

    if (startCall) {
      // eslint-disable-next-line no-use-before-define
      startConference();
      // eslint-disable-next-line no-use-before-define
      setupSubscription();
      timer = setInterval(updateCallTime, 1000);
    } else {
      manageSubscriptionAndTimer();
    }

    return () => {
      manageSubscriptionAndTimer();
    };
  }, [startCall]);

  useEffect(() => {
    if (taskToggle) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [taskToggle]);

  useEffect(() => {
    axios
      .get("/account_active_agents", {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((res) =>
        setAgents(
          res.data.map((agent) => ({
            label: agent.name,
            value: agent.uuid,
            email: agent.email,
          })),
        ),
      )
      .catch(console.log);
  }, []);

  useEffect(() => {
    const nextId = conferenceCall?.next_participant?.id;

    if (nextParticipantId !== nextId) {
      getPhoneStatuses();
      setNextParticipantId(nextId);
    }
  }, [conferenceCall?.next_participant?.id]);

  const isDnc = () => api.isDnc(currentPhoneNumber, dncStatuses);
  const isNextDnc = () => api.isDnc(nextPhoneNumber, dncStatuses);

  const fetchCurrentConferenceState = () => {
    axios.get(`${dialerServiceUrl}/dialer/fetch_dialer_state?token=${dialerServiceToken}`).then((resp) => {
      setConferenceCall(resp.data);
    });
  };

  const setupSubscription = () => {
    if (typeof App !== "undefined") {
      const websocketConnected = () => {
        setWebsocketsConnected(true);
        if (possibleStaleState) {
          fetchCurrentConferenceState();
        }
      };

      const websocketDisconnected = () => {
        setWebsocketsConnected(false);
        setPossibleStaleState(true);
      };

      App.dialer_cable = ActionCable.createConsumer(`${dialerServiceUrl}/cable?token=${dialerServiceToken}`);
      App.phone_call = App.dialer_cable.subscriptions.create("ConferenceChannel", {
        connected: function connected() {
          // eslint-disable-next-line react/no-this-in-sfc
          this.followCurrentCall();
          websocketConnected();
        },
        received: function received(data) {
          setConferenceCall(data.conference);
        },
        disconnected: function disconnected() {
          websocketDisconnected();
        },
        rejected: function rejected() {
          websocketDisconnected();
        },
        failed: function failed() {
          websocketDisconnected();
        },
        followCurrentCall: function followCurrentCall() {
          // eslint-disable-next-line react/no-this-in-sfc
          this.perform("follow");
        },
      });
    }
  };

  // eslint-disable-next-line consistent-return
  const getPerson = async (uuid) => {
    const abortController = new AbortController();

    setPersonLoading(true);
    const [res, err] = await tryCatchHandlr(
      axios.get(`/api/v4/person/${uuid}/dialer_details`, { signal: abortController.signal }),
    );

    if (err) {
      return console.error(err);
    }

    setPerson({
      ...res.data.person,
      leadId: res.data.lead_id,
    });
    setPersonLoading(false);
  };

  if (App?.leadUpdateChannel) {
    App.leadUpdateChannel.received = (data) => {
      if (isPersonStatusLoading) {
        // for status
        if (data.status === "success") {
          setPerson(
            produce(person, (draft) => {
              // eslint-disable-next-line no-param-reassign
              draft.data.attributes.status = updatedStatus;
            }),
          );
          setSavingLeadError(null);
        } else {
          setSavingLeadError(data.message);
        }
        setPersonStatusLoading(false);
      }
      // for everything other than status
      if (data.status === "failed") setSavingLeadError(data.message);
    };
  }

  const setStatus = async (newStatus) => {
    setPersonStatusLoading(true);
    setSavingLeadError(null);
    setUpdatedStatus(newStatus);

    window.App.leadUpdateChannel.perform("lead_update", {
      id: person.leadId,
      person_attributes: {
        status: newStatus,
      },
    });
  };

  const startConference = () => {
    axios
      .post(`${dialerServiceUrl}/dialer/start_dialer_session`, {
        token: dialerServiceToken,
      })
      .then((resp) => {
        getPerson(resp.data.current_participant?.person_uuid);
        setConferenceCall(resp.data);
      });
  };

  const hangUpCall = () => {
    setEndCallLoading(true);
    axios
      .post(`${dialerServiceUrl}/dialer/end_current_call`, {
        token: dialerServiceToken,
      })
      .then((resp) => {
        setConferenceCall(resp.data);
        setEndCallLoading(false);
        setEndCallErrors([]);
      })
      .catch(() => {
        setEndCallLoading(false);
        setEndCallErrors(["There was an error ending the current call, please try again."]);
      });
  };

  const dialNextLead = () => {
    setRecorded(false);
    axios.post(`${dialerServiceUrl}/dialer/dial_next_lead`, { token: dialerServiceToken }).then((resp) => {
      getPerson(resp.data.current_participant?.person_uuid);
      setConferenceCall(resp.data);
      setSavingOutcomeSuccess(null);
    });
  };

  const handleOutcomeInputChange = (key, value) => {
    setCallOutcome({ ...callOutcome, [key]: value });
  };

  const endDialerSession = () => {
    setEndSessionLoading(true);
    let callOutcomePromise = Promise.resolve();
    // eslint-disable-next-line no-use-before-define
    if (callOutcome.outcome) callOutcomePromise = submitOutcome(true);
    callOutcomePromise.finally(() => {
      axios
        .post(`${dialerServiceUrl}/dialer/end_dialer_session`, {
          token: dialerServiceToken,
        })
        .then((resp) => {
          setConferenceCall(resp.data);
          setEndSessionLoading(false);
          closeCallModal();
        })
        .then(() => {
          window.location.reload();
        });
    });
  };

  const onSkip = () => axios.post(`${dialerServiceUrl}/dialer/skip_contact`, { token: dialerServiceToken });

  const onBack = () =>
    axios.post(`${dialerServiceUrl}/dialer/back_to_contact`, { token: dialerServiceToken });

  const submitOutcome = async (pause = false) => {
    setSavingCallOutcome(true);

    if (task) {
      await createTask({
        formData: {
          ...task,
          account_id: currentUser.account.uuid,
          taskable_id: conferenceCall?.current_participant?.person_uuid,
          taskable_name:
            conferenceCall?.current_participant?.person_full_name.trim().length > 0
              ? conferenceCall?.current_participant?.person_full_name
              : "No Name",
          taskable_type: "Person",
          user_id: currentUser.uuid,
        },
      });
      setTaskToggle(false);
    }

    return axios
      .post(`${dialerServiceUrl}/dialer/save_outcome`, {
        token: dialerServiceToken,
        participant_id: conferenceCall?.current_participant?.id,
        ...callOutcome,
      })
      .then((resp) => {
        setConferenceCall(resp.data);
        setCurrentCallTime(null);
        setCallOutcome({ outcome: null, comment: "" });
        setSavingOutcomeErrors([]);
        setSavingCallOutcome(false);
        setSavingOutcomeSuccess(true);
        if (pause) return;
        if (
          conferenceCall?.current_participant_position < conferenceCall?.total_participants &&
          conferenceCall?.conference_status !== "conference-end"
        ) {
          dialNextLead();
        } else if (conferenceCall?.conference_status !== "conference-end") {
          endDialerSession();
        }
      })
      .catch(() => {
        setSavingCallOutcome(false);
        setSavingOutcomeErrors(["There was an error saving the call outcome. Please try again."]);
      });
  };

  const websocketError = () =>
    conferenceCall && conferenceCall?.conference_status !== "conference-end" && !websocketsConnected;

  const renderWebsocketError = () => (
    <div className="alert alert-danger">
      <div className="text-center">
        <i className="fa fa-exclamation-triangle tw-text-30px" />
        <br />
        <span className="tw-text-20px tw-font-bold">Connection Unstable</span>
        <br />
        <span>Your browser has lost connection with Brivity Dialer.</span>
        <br />
        <span>Please wait while we attempt to reconnect...</span>
        <br />
        <i className="fa fa-spinner fa-pulse" />
      </div>
    </div>
  );

  const renderLeadError = () => (
    <div className="alert alert-danger">
      <div className="text-center">
        <span>{savingLeadError}</span>
      </div>
    </div>
  );

  const conferenceCallIsComplete = () =>
    conferenceCall && conferenceCall?.conference_status === "conference-end" && savingOutcomeSuccess;

  const renderConferenceComplete = () => (
    <div className="tw-mt-[24px] alert alert-info">
      <div className="text-center">
        <i className="fa fa-phone tw-text-30px" />
        <br />
        <span className="tw-text-20px tw-font-bold">Dialer Session Complete</span>
      </div>
    </div>
  );

  const endCallBtnDisabled = () => {
    const callStatus = conferenceCall?.current_participant?.humanized_call_status;
    const conferenceStatus = conferenceCall?.conference_status;

    return (
      endCallLoading ||
      conferenceStatus === "conference-end" ||
      !["Queued", "Initiated", "Ringing", "In-progress"].includes(callStatus)
    );
  };

  const saveContinueBtnDisabled = () => {
    const callComplete = ["Completed", "Failed", "Busy", "No-answer", "Canceled"].includes(
      conferenceCall?.current_participant?.humanized_call_status,
    );
    return !callComplete || savingCallOutcome || callOutcome.outcome === null;
  };

  const saveBtnText = () => {
    if (!conferenceCall && conferenceCallIsComplete()) return "Save & Complete";

    if (
      conferenceCall?.current_participant_position < conferenceCall?.total_participants &&
      conferenceCall?.conference_status !== "conference-end"
    ) {
      return "Save & Continue";
    }
    return "Save & Complete";
  };

  const conferenceIsReady = () =>
    websocketsConnected &&
    conferenceCall &&
    conferenceCall?.current_participant &&
    !conferenceCallIsComplete();

  const isCompleted = callCompleteStatus.includes(conferenceCall?.current_participant?.humanized_call_status);

  const renderCallStatus = () => (
    <div className="phone-call tw-flex-1 tw-flex tw-flex-col tw-overflow-hidden">
      <div className="tw-flex tw-flex-col">
        <div className="tw-flex tw-justify-between">
          <div className="form-group contact-card">
            <div className="tw-flex tw-items-center">
              {person?.data?.attributes?.lead_type !== "n/a" && (
                <div className="tw-mr-8px">
                  <IntentDetails intent={person?.data?.attributes?.lead_type} />
                </div>
              )}
              <a
                href={conferenceCall?.current_participant?.person_url}
                target="_blank"
                rel="noreferrer"
                className="tw-text-18d tw-text-gray-50 tw-font-bold"
              >
                <span className="tw-mr-[6px]">
                  {nameFormat(conferenceCall?.current_participant?.person_full_name)}
                </span>
                <ArrowUpRightFromSquareSolidV6 className="tw-text-theme-primary" size="l" />
              </a>
            </div>
            <div className="tw-text-14d tw-font-semibold tw-text-[#999999] overflow tw-flex tw-items-center tw-gap-[4px] tw-mt-8px">
              <span>
                {phoneDetails?.category ? `${capitalize(phoneDetails.category)}:` : ""}{" "}
                {conferenceCall?.current_participant?.phone_number}
              </span>
              <DncFlag show={isDnc()} />
              <TwoPartyFlag show={conferenceCall?.current_participant?.recording_consent_required} />
              {person?.included && (
                <PhoneStatusIcon callIcon={callIcon.icon({ size: "m" })} tooltip={callIcon.tooltip} />
              )}
            </div>
          </div>
          <div className="tw-w-[208px]">
            <div className="tw-text-12d tw-text-gray-50 tw-mb-5px">
              Contact {conferenceCall?.current_participant_position || 0} of{" "}
              {conferenceCall?.total_participants || 0}
            </div>
            <div className="tw-w-full tw-h-8px tw-rounded-[16px] tw-bg-[#000000] tw-bg-opacity-20 tw-overflow-hidden">
              <div
                role="progressbar"
                aria-label="Call Progress"
                className="progress-bar call-status tw-bg-theme-primary-alternative"
                aria-valuenow={1}
                aria-valuemin="0"
                aria-valuemax="100"
                style={{
                  width: `${((conferenceCall?.current_participant_position / conferenceCall?.total_participants) * 100).toFixed()}%`,
                }}
              />
            </div>
          </div>
        </div>
        <div className="tw-flex tw-gap-20px">
          <div className="tw-w-[448px] tw-mr-auto">
            <CallStatusBar
              callStatus={conferenceCall?.current_participant?.humanized_call_status}
              currentCallTime={currentCallTime}
              isCompleted={isCompleted}
              recorded={recorded}
              recordToggleDisabled={recordToggleDisabled}
              // eslint-disable-next-line consistent-return
              onToggleChange={() => toggleRecording()}
              agentPhoneNumber={conferenceCall?.agent_participant?.phone_number}
            />
          </div>
          <Button
            disabled={endCallBtnDisabled()}
            onClick={hangUpCall}
            size="medium"
            schema="solid-red"
            className="tw-whitespace-nowrap tw-inline-flex tw-items-center tw-gap-[4px]"
          >
            <PhoneHangupSolidV6 size="m" />
            End Call
          </Button>
        </div>
        <hr className="tw-w-full tw-border-gray-10 tw-m-0 tw-mt-24px tw-mb-16px" />
      </div>
      <div className="tw-flex-1 tw-overflow-y-auto" ref={scrollRef}>
        {[...savingOutcomeErrors, ...endCallErrors].map((e) => (
          <div key={e} className="alert alert-danger">
            {e}
          </div>
        ))}
        <div className="tw-flex tw-gap-24px tw-mb-24px tw-pl-2px">
          <div className="tw-w-[140px]">
            <FieldLabel className="tw-mb-8px" label="Status" />
            <StatusDropdown
              person={{}}
              status={person?.data?.attributes?.status}
              statusOptions={person?.meta?.status_options}
              btnClassName="tw-m-0 tw-w-full"
              onChange={setStatus}
              isLoading={isPersonLoading || isPersonStatusLoading}
            />
          </div>
          <div className="tw-w-[163px]">
            <FieldLabel className="tw-mb-8px" label="Stage" />
            <StagesDropDown
              selectedStage={person?.data?.attributes?.stage}
              isLoading={isPersonLoading}
              onUpdateStage={(stage) =>
                window.App.leadUpdateChannel.perform("lead_update", {
                  id: person.leadId,
                  person_attributes: {
                    stage: stage.value,
                    stage_type: stage.type,
                  },
                })
              }
              containerClassName="!tw-border-[1.5px] !tw-border-solid !tw-border-gray-50 hover:!tw-border-gray-50 tw-w-full tw-justify-center"
            />
          </div>
        </div>
        <CallOutcomeAndNotes
          currentParticipantId={conferenceCall?.current_participant?.person_uuid}
          callOutcome={callOutcome}
          onFieldChange={handleOutcomeInputChange}
        />
        <Toggle
          text="Add Follow Up Task"
          labelPlacement="end"
          labelClassName="tw-mb-8px tw-mt-24px"
          onChange={() => {
            setTaskToggle(!taskToggle);
          }}
          checked={taskToggle}
        />
        {taskToggle && <AddTaskPane agents={agents} initialUserUuid={currentUser.uuid} setState={setTask} />}
      </div>
    </div>
  );

  return (
    <>
      {renderCallStatus()}
      {conferenceCallIsComplete() && renderConferenceComplete()}
      {websocketError() && renderWebsocketError()}
      {savingLeadError && renderLeadError()}
      <hr className="tw-w-full tw-border-gray-10 tw-m-0 tw-mt-16px tw-mb-16px" />

      {conferenceIsReady() && conferenceCall?.next_participant && (
        <UpNext
          dnc={isNextDnc()}
          twoParty={conferenceCall?.next_participant?.recording_consent_required}
          pdpLink={conferenceCall?.next_participant?.person_url}
          phoneNumber={formatPhoneNumber(conferenceCall?.next_participant?.phone_number)}
          name={nameFormat(conferenceCall?.next_participant?.person_full_name)}
          onBack={
            conferenceCall?.next_participant_position - conferenceCall?.current_participant_position > 1
              ? onBack
              : null
          }
          onSkip={
            (conferenceCall?.total_participants - conferenceCall?.next_participant_position || 0) < 1
              ? null
              : onSkip
          }
          callIcon={callIcon.icon({ size: "m" })}
          tooltip={callIcon.tooltip}
          isDataFetched={person?.included}
        />
      )}

      <Modal.Footer style={{ padding: 0 }} className="tw-flex tw-justify-between">
        <Button schema="tertiary" onClick={endDialerSession} isLoading={endSessionLoading} size="medium">
          End Session
        </Button>
        <Button
          disabled={saveContinueBtnDisabled()}
          onClick={() => submitOutcome()}
          isLoading={savingCallOutcome}
          size="medium"
        >
          {saveBtnText()}
        </Button>
      </Modal.Footer>

      <RecordConfirmationModal
        show={showConsentModal}
        closeModal={() => {
          setRecordToggleDisabled(false);
          setShowConsentModal(false);
        }}
        onClickRecord={() => {
          toggleRecording(true);
          setShowConsentModal(false);
        }}
      />
    </>
  );
};

ConferenceCallForm.propTypes = {
  dialerServiceUrl: PropTypes.string.isRequired,
  dialerServiceToken: PropTypes.string.isRequired,
  closeCallModal: PropTypes.func.isRequired,
  startCall: PropTypes.bool.isRequired,
};

export default ConferenceCallForm;
